/** @jsxImportSource @emotion/react */
import { Interpolation, Theme } from '@emotion/react';
import { ReactNode, MouseEventHandler, ReactElement } from 'react';
import { createStyles, Icon, Typography, useTheme } from '../../';
import { IconName } from '../../icons';

interface Props {
  icon?: IconName | ReactElement;
  title?: ReactNode;
  subtitle?: ReactNode;
  content?: ReactNode;
  subcontent?: ReactNode;
  action?: ReactNode;
  disabled?: boolean;
  className?: string;
  css?: Interpolation<Theme>;
  onIconClick?: MouseEventHandler<HTMLElement>;
  onClick?: MouseEventHandler<HTMLElement>;
  testId?: string;
}

export const ListItem = ({
  icon,
  title,
  subtitle,
  content,
  subcontent,
  action,
  disabled,
  className,
  css,
  onIconClick,
  onClick,
  testId,
}: Props) => {
  const { palette } = useTheme();
  const {
    root,
    textWrapper,
    pointer,
    disabledStyle,
    titleWrapper,
    contentWrapper,
  } = useStyles();

  return (
    <li
      data-testid={testId}
      onClick={(e) => !disabled && onClick && onClick(e)}
      tabIndex={!disabled && onClick ? 0 : -1}
      css={[
        root,
        !disabled && onClick && pointer,
        disabled && disabledStyle,
        css,
      ]}
      className={className}
    >
      {icon && (
        <div
          data-testid="icon"
          onClick={onIconClick}
          css={[onIconClick && pointer]}
        >
          {typeof icon === 'string' ? <Icon name={icon} /> : icon}
        </div>
      )}
      <div css={textWrapper}>
        <div css={[titleWrapper]}>
          {typeof title === 'string' ? <Typography>{title}</Typography> : title}
          {typeof subtitle === 'string' ? (
            <Typography variant="bodySmall" color={palette.grey.default}>
              {subtitle}
            </Typography>
          ) : (
            subtitle
          )}
        </div>
        {(content || subcontent) && (
          <div css={[contentWrapper]}>
            {typeof content === 'string' ? (
              <Typography>{content}</Typography>
            ) : (
              content
            )}
            {typeof subcontent === 'string' ? (
              <Typography variant="bodySmall" color={palette.grey.default}>
                {subcontent}
              </Typography>
            ) : (
              subcontent
            )}
          </div>
        )}
      </div>
      {action && <div data-testid="action">{action}</div>}
    </li>
  );
};

const useStyles = createStyles(({ spacing }) => ({
  root: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxs,
    padding: spacing.xxs,
    p: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  textWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    overflow: 'hidden',
  },
  titleWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
  },
  pointer: {
    cursor: 'pointer',
  },
  disabledStyle: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
}));
